import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { itineraryService, tripMemberService } from '../api';
import { BaseItineraryItem } from '../types';
import { TripMemberResponse } from '../api/tripMemberService';
import Sidebar from '../components/Sidebar';
import './TripDetail.css';

const TripDetail: React.FC = () => {
  const { tripId } = useParams<{ tripId: string }>();
  const { user } = useAuth();
  const [itineraryItems, setItineraryItems] = useState<BaseItineraryItem[]>([]);
  const [tripMembers, setTripMembers] = useState<TripMemberResponse[]>([]);
  const [tripName, setTripName] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tripId) {
      loadTripDetails();
    }
  }, [tripId]);

  const loadTripDetails = async () => {
    setLoading(true);

    // Load itinerary items
    try {
      const items = await itineraryService.getTripItinerary(tripId!);
      console.log('Loaded itinerary items:', items);
      setItineraryItems(items);

      // Extract trip name from the first item if available
      if (items.length > 0 && items[0].trip) {
        setTripName(items[0].trip.name || '');
        console.log('Trip name:', items[0].trip.name);
      }
    } catch (error) {
      console.error('Error loading itinerary:', error);
    }

    // Load members separately (don't let it block itinerary)
    try {
      const members = await tripMemberService.getTripMembers(tripId!);
      console.log('Loaded members:', members);
      setTripMembers(members);
    } catch (error) {
      console.error('Error loading members:', error);
    }

    setLoading(false);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getItemTypeColor = (itemType: string) => {
    const colors: { [key: string]: string } = {
      TRAVEL: '#3b82f6',
      ACCOMMODATION: '#8b5cf6',
      ACTIVITIES: '#f59e0b',
      FOOD: '#10b981'
    };
    return colors[itemType] || '#6b7280';
  };

  return (
    <div className="layout">
      <Sidebar />
      <div className="main-content">
        <div className="top-bar">
          <div className="user-info">
            <span className="user-icon">👤</span>
            <span className="user-email">{user?.email || 'admin@roameez.com'}</span>
          </div>
        </div>

        <div className="page-container">
          <div className="page-header">
            <div>
              <h1 className="page-title">{tripName || 'Trip Detail'}</h1>
              <div className="breadcrumb">
                <span>Dashboard</span>
                <span className="breadcrumb-separator">›</span>
                <span>Trips</span>
                <span className="breadcrumb-separator">›</span>
                <span>{tripId}</span>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="loading-container">Loading...</div>
          ) : (
            <>
              {/* Trip Members Section */}
              {tripMembers.length > 0 && (
                <div className="section-card">
                  <h2 className="section-title">Trip Members ({tripMembers.length})</h2>
                  <div className="members-grid">
                    {tripMembers.map((member) => (
                      <div key={member.id} className="member-card">
                        <div className="member-header">
                          {member.user?.profilePic ? (
                            <img
                              src={member.user.profilePic}
                              alt={member.user.name}
                              className="member-avatar"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                                if (placeholder) placeholder.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className="member-avatar-placeholder"
                            style={{ display: member.user?.profilePic ? 'none' : 'flex' }}
                          >
                            {member.user?.name?.charAt(0) || '?'}
                          </div>
                          <div className="member-details">
                            <div className="member-name-row">
                              <span className="member-name">{member.user?.name || 'Unknown'}</span>
                              {member.isAdmin && (
                                <span className="admin-badge">Admin</span>
                              )}
                            </div>
                            {member.user?.username && (
                              <span className="member-username">@{member.user.username}</span>
                            )}
                            <span className="member-id">ID: {member.user?.id || 'N/A'}</span>
                          </div>
                        </div>
                        <div className="member-footer">
                          <span className={`status-badge ${member.status.toLowerCase()}`}>
                            {member.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Itinerary Items Section */}
              {itineraryItems.length > 0 ? (
                <div className="section-card">
                  <h2 className="section-title">Itinerary ({itineraryItems.length})</h2>
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Type</th>
                          <th>Title</th>
                          <th>Start Date</th>
                          <th>End Date</th>
                          <th>Notes</th>
                          <th>Booking ID</th>
                          <th>Tagged Members</th>
                        </tr>
                      </thead>
                      <tbody>
                        {itineraryItems.map((item) => (
                          <tr key={item.id}>
                            <td>{item.id}</td>
                            <td>
                              <span
                                className="item-type-badge"
                                style={{ backgroundColor: getItemTypeColor(item.itemType) }}
                              >
                                {item.itemType}
                              </span>
                            </td>
                            <td className="item-title-cell">{item.title}</td>
                            <td>{formatDateTime(item.startDateTime)}</td>
                            <td>{item.endDateTime ? formatDateTime(item.endDateTime) : '-'}</td>
                            <td className="item-notes-cell">{item.notes || '-'}</td>
                            <td>{item.bookingId || '-'}</td>
                            <td>{item.members?.length || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="no-data">
                  <p>No itinerary items found for this trip.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TripDetail;
